(() => {
  if (customElements.get('hp-category-navigation')) return;

  class HpCategoryNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.groups = [...this.querySelectorAll('[data-hp-nav-group]')];
      this.branches = this.querySelector('.hp-nav__branches');
      this.mobile = this.querySelector('[data-hp-nav-select]');
      this.catalogHeading = this.querySelector('.hp-nav__catalog-heading');
      this.wide = window.matchMedia('(min-width: 750px)');
      this.selected = -1;
      this.total = this.groups.reduce((sum, group) => sum + Number(group.dataset.hpNavGroupCount), 0);
      const entries = [{ name: this.dataset.allLabel, hint: this.dataset.allHint, count: this.total,
        controls: this.querySelector('.hp-nav__groups').id }, ...this.groups.map((group) => ({
        name: group.querySelector('.hp-nav__group-name').textContent.trim(), hint: group.dataset.hint,
        count: Number(group.dataset.hpNavGroupCount), controls: group.querySelector('.hp-nav__panel').id,
      }))];
      this.buttons = entries.map((entry, position) => {
        const index = position - 1;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hp-nav__branch';
        button.setAttribute('aria-controls', entry.controls);
        const copy = document.createElement('span');
        copy.className = 'hp-nav__branch-copy';
        const name = document.createElement('strong');
        name.textContent = entry.name;
        const hint = document.createElement('span');
        hint.textContent = entry.hint;
        const count = document.createElement('span');
        count.className = 'hp-nav__branch-count';
        count.textContent = String(entry.count);
        copy.append(name, hint);
        button.append(copy, count);
        button.addEventListener('click', () => this.select(index), { signal });
        this.branches.append(button);
        if (index >= 0) {
          const option = document.createElement('option');
          option.value = String(index);
          option.textContent = entry.name;
          this.mobile.append(option);
          this.groups[index].querySelector('summary').addEventListener('click', (event) => {
            event.preventDefault();
            this.select(index, true);
          }, { signal });
        }
        return button;
      });
      this.mobile.addEventListener('change', () => this.select(Number(this.mobile.value)), { signal });
      this.wide.addEventListener('change', () => this.updateMode(), { signal });
      this.dataset.enhanced = 'true';
      this.catalogHeading.hidden = false;
      this.updateMode();
    }

    select(index, moveFocus = false) {
      if (index < -1 || index >= this.groups.length) return;
      this.selected = index;
      const all = index === -1;
      this.dataset.overview = String(all);
      this.mobile.value = String(index);
      this.groups.forEach((group, i) => { group.open = all || i === index; });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i - 1 === index)));
      this.querySelector('[data-hp-nav-title]').textContent = all ? this.dataset.allLabel :
        this.groups[index].querySelector('.hp-nav__group-name').textContent;
      const count = all ? this.total : Number(this.groups[index].dataset.hpNavGroupCount);
      this.querySelector('[data-hp-nav-count]').textContent = `${count} ${count === 1 ? this.dataset.categoryLabel : this.dataset.categoriesLabel}`;
      if (moveFocus) {
        (this.wide.matches ? this.buttons[index + 1] : this.mobile).focus({ preventScroll: true });
        if (this.dataset.variant === 'page') {
          (this.wide.matches ? this.catalogHeading : this.mobile.closest('label')).scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }
    }

    updateMode() {
      this.branches.hidden = !this.wide.matches;
      this.mobile.closest('label').hidden = this.wide.matches;
      this.select(this.selected);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.branches?.replaceChildren();
      this.mobile?.querySelectorAll('option:not([value="-1"])').forEach((option) => option.remove());
      if (this.mobile) this.mobile.closest('label').hidden = true;
      if (this.branches) this.branches.hidden = true;
      if (this.catalogHeading) this.catalogHeading.hidden = true;
      this.groups?.forEach((group) => { group.open = true; });
      delete this.dataset.enhanced;
      delete this.dataset.overview;
    }
  }
  customElements.define('hp-category-navigation', HpCategoryNavigation);

  const desktop = window.matchMedia('(min-width: 990px)');
  const hooks = ['on:focus', 'on:blur', 'on:pointerenter', 'on:pointerleave'];
  const managed = new Map();
  let activeTrigger = null;
  let openMode = null;
  let openTimer;
  let closeTimer;
  let refreshFrame;
  // The collection index is already the complete navigation surface.
  const dropdown = () => document.querySelector('hp-category-navigation[data-variant="page"]') ? null : document.querySelector('[data-hp-nav-dropdown]');
  if (!dropdown()) return;

  function routeMatches(link) {
    try {
      const url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin) return false;
      const path = url.pathname.replace(/\/+$/, '');
      const nativePath = new URL(dropdown()?.dataset.collectionsUrl || '/collections', window.location.href).pathname.replace(/\/+$/, '');
      return path === nativePath || /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?collections$/i.test(path);
    } catch { return false; }
  }

  function eligible(link) {
    const header = link?.closest('#header-component');
    const item = link?.closest('li.menu-list__list-item');
    return Boolean(dropdown() && desktop.matches && header?.dataset.menuStyle === 'menu' && item &&
      !item.matches('[slot="overflow"], [aria-hidden="true"]') &&
      link.closest('header-menu') && link.getClientRects().length && routeMatches(link));
  }

  function triggerFrom(event) {
    for (const target of event.composedPath()) {
      if (!(target instanceof Element)) continue;
      const link = target.closest('a.menu-list__link[href]');
      if (link && eligible(link)) return link;
    }
    return null;
  }

  // Horizon's delegated native hooks otherwise overwrite this disclosure's ARIA.
  // Preserve and suppress only Products' four hooks while our desktop panel applies.
  function manage(link) {
    const item = link.closest('li.menu-list__list-item');
    if (!managed.has(link)) {
      managed.set(link, {
        item,
        hooks: new Map(hooks.map((name) => [name, item.getAttribute(name)])),
        expanded: link.getAttribute('aria-expanded'),
        controls: link.getAttribute('aria-controls'),
      });
    }
    hooks.forEach((name) => item.removeAttribute(name));
    link.setAttribute('aria-controls', dropdown().id);
    link.setAttribute('aria-expanded', String(link === activeTrigger));
  }

  function restore(link, original) {
    original.hooks.forEach((value, name) => { if (value !== null) original.item.setAttribute(name, value); });
    for (const [name, value] of [['aria-expanded', original.expanded], ['aria-controls', original.controls]]) {
      if (value === null) link.removeAttribute(name); else link.setAttribute(name, value);
    }
    managed.delete(link);
  }

  function place() {
    const panel = dropdown();
    if (!panel || panel.hidden || !activeTrigger) return;
    const header = activeTrigger.closest('#header-component');
    const bottom = Math.max(0, header.getBoundingClientRect().bottom);
    panel.style.setProperty('--hp-nav-top', `${bottom}px`);
  }

  function close(restoreFocus = false) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    const previous = activeTrigger;
    const panel = dropdown();
    if (panel) panel.hidden = true;
    activeTrigger = null;
    openMode = null;
    managed.forEach((_, link) => link.setAttribute('aria-expanded', 'false'));
    if (restoreFocus && previous?.isConnected) previous.focus();
  }

  function open(link, mode, focusInside = false) {
    if (!eligible(link) || !dropdown()) return;
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    manage(link);
    activeTrigger = link;
    openMode = mode;
    managed.forEach((_, item) => item.setAttribute('aria-expanded', String(item === link)));
    const panel = dropdown();
    panel.hidden = false;
    place();
    if (focusInside) panel.querySelector('.hp-nav__branch, .hp-nav__view-all')?.focus();
  }

  function retain(target) {
    return target instanceof Node && (dropdown()?.contains(target) || activeTrigger?.contains(target));
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (openMode !== 'hover') return;
    closeTimer = setTimeout(() => {
      if (!dropdown()?.contains(document.activeElement)) close();
    }, 220);
  }

  function refresh() {
    refreshFrame = null;
    if (activeTrigger && (!activeTrigger.isConnected || !eligible(activeTrigger))) close();
    managed.forEach((original, link) => {
      if (!link.isConnected || !eligible(link)) restore(link, original);
    });
    if (!dropdown()) return;
    document.querySelectorAll('#header-component header-menu a.menu-list__link[href]').forEach((link) => {
      if (eligible(link)) manage(link);
    });
    place();
  }

  function queueRefresh() {
    if (!refreshFrame) refreshFrame = requestAnimationFrame(refresh);
  }

  // Window capture runs before Horizon's document-capture dispatcher, including
  // the first event after section hydration has replaced Products' native hooks.
  ['pointerenter', 'pointerleave', 'focus', 'blur'].forEach((name) => {
    window.addEventListener(name, (event) => {
      let trigger = triggerFrom(event);
      if (!trigger && event.target instanceof Element) {
        const item = event.target.closest('li.menu-list__list-item');
        const link = item?.querySelector(':scope > a.menu-list__link[href]');
        if (eligible(link)) trigger = link;
      }
      if (trigger && dropdown()) manage(trigger);
    }, true);
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const trigger = triggerFrom(event);
    if (trigger) {
      if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      if (activeTrigger === trigger) return;
      openTimer = setTimeout(() => open(trigger, 'hover'), 90);
    } else if (dropdown()?.contains(event.target)) {
      clearTimeout(closeTimer);
    }
  }, true);

  document.addEventListener('pointerout', (event) => {
    if (retain(event.relatedTarget)) return;
    if (triggerFrom(event) || dropdown()?.contains(event.target)) scheduleClose();
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = triggerFrom(event);
    if (trigger) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (activeTrigger === trigger && openMode !== 'hover') close();
      else open(trigger, event.detail === 0 ? 'keyboard' : 'click', event.detail === 0);
    } else if (event.target instanceof Element && event.target.closest('[data-hp-nav-close]')) {
      close(true);
    } else if (!retain(event.target)) {
      close();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeTrigger) {
      event.preventDefault();
      close(true);
      return;
    }
    const trigger = triggerFrom(event);
    if (trigger && (event.key === 'ArrowDown' || event.key === ' ')) {
      event.preventDefault();
      open(trigger, 'keyboard', true);
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    if (activeTrigger && !retain(event.target)) close();
  });
  window.addEventListener('resize', queueRefresh, { passive: true });
  window.addEventListener('scroll', place, { passive: true });
  desktop.addEventListener('change', queueRefresh);
  document.addEventListener('shopify:section:load', queueRefresh);
  new MutationObserver(queueRefresh).observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['data-menu-style'],
  });
  refresh();
})();
